import { Link } from "react-router-dom";
import { BookOpen, Heart, Users, Sparkles, Cross } from "lucide-react";

const PASTOR_IMG = "https://customer-assets.emergentagent.com/job_covenant-ministry/artifacts/3re2p505_S.%20Hilliard%20Profile%20Pic.jpg";
const BIBLE_STUDY_IMG = "https://images.unsplash.com/photo-1529070538774-1843cb3265df?auto=format&fit=crop&w=1200&q=80";

export default function About() {
  return (
    <div data-testid="about-page" className="pt-32 pb-20">
      <div className="ccn-container">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="ccn-overline mb-4">Our story</div>
            <h1 className="ccn-h1 mb-6">An online ministry, gathered <em className="not-italic text-[#C8664D]">in covenant.</em></h1>
            <p className="text-lg text-[#575A58] leading-relaxed mb-5">
              Christian Covenant Network exists to make the gospel of Jesus Christ accessible — anywhere, anytime, in any language of the heart. We are a digital home for worship, biblical teaching, prayer, and generous community.
            </p>
            <p className="text-[#575A58] leading-relaxed">
              From sermons that meet you in the everyday to live services that feel like home, we believe the Word travels well through screens — and so does the Spirit.
            </p>
          </div>
          <div className="relative">
            <img src={BIBLE_STUDY_IMG} alt="people in bible study" data-testid="about-hero-image" className="rounded-3xl w-full aspect-[4/5] object-cover shadow-xl" />
            <div className="absolute -bottom-6 -left-6 bg-[#DDA754] text-[#2A2E2C] rounded-2xl p-5 max-w-[200px]">
              <div className="font-heading text-2xl leading-tight">"For where two or three gather…"</div>
              <div className="text-xs uppercase tracking-[0.2em] mt-2">Matt 18:20</div>
            </div>
          </div>
        </div>

        <section className="mt-32">
          <div className="ccn-overline mb-4">Our founder</div>
          <h2 className="ccn-h2 mb-12 max-w-3xl">Meet Pastor Stacy Hilliard.</h2>
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-2">
              <div className="relative">
                <img
                  src={PASTOR_IMG}
                  alt="Pastor Stacy Hilliard"
                  data-testid="pastor-stacy-photo"
                  className="rounded-3xl w-full aspect-square object-cover shadow-xl"
                />
                <div className="absolute -bottom-5 -right-5 bg-[#C8664D] text-white rounded-2xl px-5 py-3 shadow-lg">
                  <div className="text-xs uppercase tracking-[0.25em] opacity-80">Founder &amp; Pastor</div>
                  <div className="font-heading text-xl leading-tight">Stacy Hilliard</div>
                </div>
              </div>
            </div>
            <div className="lg:col-span-3" data-testid="pastor-stacy-bio">
              <p className="text-lg text-[#1E1E1E] leading-relaxed mb-5">
                Christian Covenant Network (CCN) is a church community founded by Pastor Stacy Hilliard.
              </p>
              <p className="text-[#575A58] leading-relaxed mb-4">
                Pastor Hilliard lives in <span className="text-[#1E1E1E] font-medium">Oklahoma City, Oklahoma</span>,
                where he served in <span className="text-[#1E1E1E] font-medium">Evangelism USA</span> as
                Director of Church Multiplication for the International Pentecostal Holiness Church.
              </p>
              <p className="text-[#575A58] leading-relaxed mb-4">
                He holds a <span className="text-[#1E1E1E] font-medium">Bachelor of Practical Theology</span> and a
                <span className="text-[#1E1E1E] font-medium"> Master's Degree in Ministry</span> from Advantage College.
              </p>
              <p className="text-[#575A58] leading-relaxed mb-4">
                Prior to joining Evangelism USA, he lived in Greenville, North Carolina, where he founded
                <span className="text-[#1E1E1E] font-medium"> Breakthrough Revival Center Church</span> and
                served as a law enforcement officer.
              </p>
              <p className="text-[#575A58] leading-relaxed">
                He has been involved in church planting since 1994 and has worked with church planters and church planting groups across the country.
              </p>
            </div>
          </div>

          {/* Vision + Mission */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-16">
            <div data-testid="pastor-vision-card" className="ccn-card p-8 lg:p-10 border-l-4 border-l-[#C8664D]">
              <div className="ccn-overline text-[#C8664D] mb-4">The Vision</div>
              <p className="text-[#1E1E1E] leading-relaxed text-lg">
                Pastor Hilliard's vision for CCN is to build an
                <span className="font-medium"> inclusive, Spirit-led community </span>
                where believers connect, grow in faith, and serve others — using technology to
                empower every person to make a global impact for Christ.
              </p>
            </div>
            <div data-testid="pastor-mission-card" className="ccn-card p-8 lg:p-10 border-l-4 border-l-[#DDA754]">
              <div className="ccn-overline text-[#A8533E] mb-4">The Mission</div>
              <p className="text-[#1E1E1E] leading-relaxed text-lg">
                Pastor Hilliard's mission for CCN is to empower believers worldwide through
                <span className="text-[#C8664D] font-medium"> biblical teaching</span>,
                <span className="text-[#C8664D] font-medium"> mentorship</span>, and
                <span className="text-[#C8664D] font-medium"> inclusive fellowship</span>.
              </p>
            </div>
          </div>
        </section>

        <section className="mt-32">
          <div className="ccn-overline mb-4">What we believe</div>
          <h2 className="ccn-h2 mb-12 max-w-3xl">Five anchors that hold us steady.</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {[
              { i: Cross, t: "Jesus", d: "The crucified, risen, returning Christ — fully God, fully man, fully Lord." },
              { i: BookOpen, t: "Scripture", d: "Inspired, authoritative, sufficient. The lens through which we read life." },
              { i: Sparkles, t: "Spirit", d: "Comforter, Convicter, Counselor. Active and available today." },
              { i: Heart, t: "Grace", d: "We are saved by grace through faith — not by our striving." },
              { i: Users, t: "Mission", d: "Every believer sent. Every neighbor loved. Every nation reached." },
            ].map((v) => (
              <div key={v.t} className="ccn-card p-6">
                <div className="w-12 h-12 rounded-full bg-[#C8664D]/10 text-[#C8664D] flex items-center justify-center mb-4">
                  <v.i className="w-6 h-6" />
                </div>
                <div className="font-heading text-xl mb-2">{v.t}</div>
                <div className="text-sm text-[#575A58] leading-relaxed">{v.d}</div>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-32 ccn-card p-10 lg:p-16 text-center">
          <div className="ccn-overline mb-4">Get connected</div>
          <h2 className="ccn-h2 mb-5 max-w-2xl mx-auto">Whether you're new or returning — there's a place for you here.</h2>
          <p className="text-[#575A58] mb-8 max-w-xl mx-auto">
            Join our weekly updates and never miss a sermon, prayer night or community event.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/live" className="ccn-btn-primary">Watch live</Link>
            <Link to="/sermons" className="ccn-btn-ghost">Browse sermons</Link>
          </div>
        </section>
      </div>
    </div>
  );
}
